import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Markdown, Seletor } from "../componentes/conhecimento";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../componentes/ui";
import {
  ACOES_DO_ESTADO,
  ROTULO_DA_ACAO,
  ROTULO_DA_ORIGEM,
  ROTULO_DA_VISIBILIDADE,
  ROTULO_DO_ESTADO_DO_ARTIGO,
  tomDoEstadoDoArtigo,
  useAcaoNoArtigo,
  useArtigoNoConsole,
  useAvaliacoesDoArtigo,
  useCategoriasDeArtigo,
  useCriarArtigo,
  useEditarArtigo,
  useModulosDoCatalogo,
  useOrganizacoesParaArtigo,
  useVersaoDoArtigo,
  type ArtigoNoConsole,
  type CorpoDoArtigo,
  type Visibilidade,
} from "../nucleo/conhecimento";
import { mensagemDoConsole } from "../nucleo/console";
import { enderecoSeguro } from "../nucleo/markdown";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";

const EM_BRANCO: CorpoDoArtigo = {
  titulo: "",
  resumo: "",
  corpo: "",
  categoria_id: null,
  modulo_chave: null,
  visibilidade: "publica",
  organizacao_id: null,
};

function dataHora(valor: string | null): string {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

/**
 * Título, texto e a fronteira do artigo, com a pré-visualização ao lado.
 *
 * A pré-visualização é o mesmo `Markdown` da Central: desenha elementos, nunca injeta HTML.
 * Quem escreve `<script>` vê o texto `<script>` — e é exatamente o que o cliente verá.
 */
function FormularioDoArtigo({
  inicial,
  desabilitado,
  rotuloDoBotao,
  salvando,
  erro,
  aoSalvar,
}: {
  inicial: CorpoDoArtigo;
  desabilitado: boolean;
  rotuloDoBotao: string;
  salvando: boolean;
  erro: unknown;
  aoSalvar: (corpo: CorpoDoArtigo) => void;
}) {
  const [campos, setCampos] = useState(inicial);
  const categorias = useCategoriasDeArtigo();
  const modulos = useModulosDoCatalogo();
  const organizacoes = useOrganizacoesParaArtigo(campos.visibilidade === "organizacao");
  const mudar = (parcial: Partial<CorpoDoArtigo>) => setCampos((atuais) => ({ ...atuais, ...parcial }));

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    aoSalvar({
      ...campos,
      titulo: campos.titulo.trim(),
      resumo: campos.resumo.trim(),
      corpo: campos.corpo.trim(),
      organizacao_id: campos.visibilidade === "organizacao" ? campos.organizacao_id : null,
    });
  }

  const faltaEmpresa = campos.visibilidade === "organizacao" && !campos.organizacao_id;
  const incompleto = campos.titulo.trim().length < 3 || !campos.corpo.trim() || faltaEmpresa;

  return (
    <form onSubmit={enviar} data-testid="formulario-do-artigo">
      <div className="grid gap-3 md:grid-cols-2">
        <Campo
          rotulo="Título"
          value={campos.titulo}
          maxLength={200}
          disabled={desabilitado}
          onChange={(evento) => mudar({ titulo: evento.target.value })}
        />
        <Campo
          rotulo="Resumo (aparece nas listas e na busca)"
          value={campos.resumo}
          maxLength={500}
          disabled={desabilitado}
          onChange={(evento) => mudar({ resumo: evento.target.value })}
        />
        <Seletor
          rotulo="Categoria"
          valor={campos.categoria_id ?? ""}
          desabilitado={desabilitado}
          aoMudar={(valor) => mudar({ categoria_id: valor || null })}
        >
          <option value="">Sem categoria</option>
          {(categorias.data ?? []).map((categoria) => (
            <option value={categoria.id} key={categoria.id}>
              {categoria.nome}
            </option>
          ))}
        </Seletor>
        <Seletor
          rotulo="Módulo"
          valor={campos.modulo_chave ?? ""}
          desabilitado={desabilitado}
          aoMudar={(valor) => mudar({ modulo_chave: valor || null })}
          ajuda="O cliente que não tem o módulo não vê o artigo na busca filtrada."
        >
          <option value="">Geral (vale para todos)</option>
          {(modulos.data ?? []).map((modulo) => (
            <option value={modulo.chave} key={modulo.chave}>
              {modulo.nome}
            </option>
          ))}
        </Seletor>
        <Seletor
          rotulo="Quem pode ler"
          valor={campos.visibilidade}
          desabilitado={desabilitado}
          aoMudar={(valor) =>
            mudar({
              visibilidade: valor as Visibilidade,
              organizacao_id: valor === "organizacao" ? campos.organizacao_id : null,
            })
          }
        >
          {Object.entries(ROTULO_DA_VISIBILIDADE).map(([valor, rotulo]) => (
            <option value={valor} key={valor}>
              {rotulo}
            </option>
          ))}
        </Seletor>
        {campos.visibilidade === "organizacao" ? (
          <Seletor
            rotulo="Empresa"
            valor={campos.organizacao_id ?? ""}
            desabilitado={desabilitado}
            aoMudar={(valor) => mudar({ organizacao_id: valor || null })}
            ajuda="Só as pessoas desta empresa encontram o artigo."
          >
            <option value="">Escolha a empresa</option>
            {(organizacoes.data ?? []).map((organizacao) => (
              <option value={organizacao.id} key={organizacao.id}>
                {organizacao.nome_fantasia ?? organizacao.razao_social}
              </option>
            ))}
          </Seletor>
        ) : null}
      </div>

      <div className="editor-do-artigo editor-do-artigo--lado-a-lado mt-3">
        <CampoLongo
          rotulo="Texto do artigo (markdown)"
          ajuda="Use # para títulos, ** para negrito, - para listas e [texto](https://...) para links. HTML não é aceito: aparece como texto."
          value={campos.corpo}
          rows={16}
          maxLength={50000}
          disabled={desabilitado}
          onChange={(evento) => mudar({ corpo: evento.target.value })}
        />
        <div>
          <h2 className="titulo-secao">Pré-visualização</h2>
          <div className="previa-do-artigo" data-testid="previa-do-artigo">
            {campos.corpo.trim() ? (
              <Markdown texto={campos.corpo} />
            ) : (
              <p className="nota-rodape">O texto aparece aqui enquanto você escreve.</p>
            )}
          </div>
        </div>
      </div>

      {erro ? (
        <Aviso tom="erro" className="mt-3">
          {mensagemDoConsole(erro)}
        </Aviso>
      ) : null}
      <div className="mt-4">
        <Botao type="submit" disabled={desabilitado || incompleto || salvando}>
          {salvando ? "Salvando…" : rotuloDoBotao}
        </Botao>
      </div>
    </form>
  );
}

export function NovoArtigo() {
  useTitulo("Novo artigo");
  const criar = useCriarArtigo();
  const navegar = useNavigate();

  return (
    <div data-testid="novo-artigo">
      <p className="nota-rodape">
        <Link to="/admin/conhecimento">Base de conhecimento</Link>
      </p>
      <h1 className="titulo-pagina">Novo artigo</h1>
      <p className="lead">O artigo nasce como rascunho. Só vai para os clientes depois de revisado e publicado.</p>
      <FormularioDoArtigo
        inicial={EM_BRANCO}
        desabilitado={false}
        rotuloDoBotao="Salvar rascunho"
        salvando={criar.isPending}
        erro={criar.error}
        aoSalvar={(corpo) =>
          criar.mutate(corpo, { onSuccess: (artigo) => navegar(`/admin/conhecimento/${artigo.id}`) })
        }
      />
    </div>
  );
}

function Versoes({ artigo }: { artigo: ArtigoNoConsole }) {
  const [aberta, setAberta] = useState<number | null>(null);
  const versao = useVersaoDoArtigo(artigo.id, aberta);

  return (
    <Cartao titulo="Histórico de versões" className="mt-5" data-testid="versoes-do-artigo">
      <ul className="lista-de-itens">
        {artigo.versoes.map((item) => (
          <li key={item.numero} data-testid={`versao-${item.numero}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <strong>Versão {item.numero}</strong>
                {item.numero === artigo.versao_atual ? " (atual)" : ""}
                <p className="nota-rodape">
                  {item.autor ?? "Autor removido"} · criada em {dataHora(item.criada_em)}
                  {item.publicada_em ? ` · publicada em ${dataHora(item.publicada_em)}` : " · não publicada"}
                </p>
              </div>
              <Botao
                tamanho="pequeno"
                variante="fantasma"
                aria-label={`${aberta === item.numero ? "Fechar" : "Ler"} a versão ${item.numero}`}
                onClick={() => setAberta(aberta === item.numero ? null : item.numero)}
              >
                {aberta === item.numero ? "Fechar" : "Ler"}
              </Botao>
            </div>
          </li>
        ))}
      </ul>
      {aberta !== null ? (
        <div className="mt-4 border-t border-borda pt-4" data-testid="versao-aberta">
          {versao.isPending ? (
            <p role="status">Abrindo a versão…</p>
          ) : versao.isError ? (
            <Aviso tom="erro">{mensagemDoConsole(versao.error)}</Aviso>
          ) : (
            <>
              <h3 className="titulo-secao">
                {versao.data.titulo} — versão {versao.data.numero} (somente leitura)
              </h3>
              <Markdown texto={versao.data.corpo} />
            </>
          )}
        </div>
      ) : null}
    </Cartao>
  );
}

function Avaliacoes({ artigoId }: { artigoId: string }) {
  const avaliacoes = useAvaliacoesDoArtigo(artigoId);
  return (
    <Cartao titulo="Avaliações dos clientes" className="mt-5" data-testid="avaliacoes-do-artigo">
      {avaliacoes.isPending ? (
        <p role="status">Carregando avaliações…</p>
      ) : avaliacoes.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(avaliacoes.error)}</Aviso>
      ) : avaliacoes.data.length === 0 ? (
        <p className="nota-rodape">Ninguém avaliou este artigo ainda.</p>
      ) : (
        <ul className="lista-de-itens">
          {avaliacoes.data.map((avaliacao, indice) => (
            <li key={indice}>
              <strong>{avaliacao.util ? "Ajudou" : "Não ajudou"}</strong>
              <span className="nota-rodape">
                {" "}
                · {avaliacao.pessoa}, {avaliacao.organizacao} · versão {avaliacao.versao} ·{" "}
                {dataHora(avaliacao.atualizada_em)}
              </span>
              {avaliacao.comentario ? <p className="texto-preservado">{avaliacao.comentario}</p> : null}
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}

function CorpoDoEditor({ artigo }: { artigo: ArtigoNoConsole }) {
  const sessao = useSessao();
  const podePublicar = podeNaSessao(sessao.data, "conhecimento:publicar");
  const editar = useEditarArtigo(artigo.id);
  const agir = useAcaoNoArtigo(artigo.id);
  const [recado, setRecado] = useState<string | null>(null);

  const origemAberta = artigo.origem_url ? enderecoSeguro(artigo.origem_url) : null;
  const noAr = artigo.estado === "publicado";
  const arquivado = artigo.estado === "arquivado";
  const semPermissao = noAr && !podePublicar;
  const desabilitado = arquivado || semPermissao;

  return (
    <div data-testid={`editor-do-artigo-${artigo.id}`}>
      <p className="nota-rodape">
        <Link to="/admin/conhecimento">Base de conhecimento</Link>
      </p>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h1 className="titulo-pagina mb-0">{artigo.titulo}</h1>
        <Chip estado={tomDoEstadoDoArtigo(artigo.estado)} data-testid="estado-do-artigo">
          {ROTULO_DO_ESTADO_DO_ARTIGO[artigo.estado]}
        </Chip>
      </div>
      <p className="nota-rodape mb-4">
        Versão {artigo.versao_atual} · lido por: {ROTULO_DA_VISIBILIDADE[artigo.visibilidade]}
        {artigo.organizacao ? ` (${artigo.organizacao})` : ""} · autor: {artigo.autor ?? "—"}
        {artigo.revisor ? ` · revisor: ${artigo.revisor}` : ""}
        {artigo.modulo ? ` · módulo: ${artigo.modulo}` : ""}
        {artigo.origem_ticket_id ? (
          <>
            {" · criado a partir do "}
            <Link to={`/admin/chamados/${artigo.origem_ticket_id}`}>chamado de origem</Link>
          </>
        ) : null}
      </p>

      {artigo.origem ? (
        <Aviso tom="neutro" className="mb-4" data-testid="origem-do-artigo">
          Importado de: <b>{ROTULO_DA_ORIGEM[artigo.origem] ?? artigo.origem}</b>
          {artigo.origem_atualizada_em
            ? `, atualizado na origem em ${dataHora(artigo.origem_atualizada_em)}`
            : ""}
          .{" "}
          {origemAberta ? (
            <a href={origemAberta} target="_blank" rel="noopener noreferrer">
              Abrir na origem
            </a>
          ) : null}{" "}
          Se a origem mudar, a próxima importação cria a versão seguinte
          {artigo.estado === "publicado" ? " e devolve o artigo para revisão" : ""}.
        </Aviso>
      ) : null}
      {noAr && podePublicar ? (
        <Aviso tom="atencao" className="mb-4">
          Este artigo está no ar. Salvar uma mudança no texto cria a versão {artigo.versao_atual + 1}
          , que vai direto para os clientes; a versão atual continua no histórico.
        </Aviso>
      ) : null}
      {semPermissao ? (
        <Aviso tom="atencao" className="mb-4">
          Este artigo já está publicado. Só quem tem permissão para publicar pode editá-lo.
        </Aviso>
      ) : null}
      {arquivado ? (
        <Aviso tom="neutro" className="mb-4">
          Artigo arquivado: fora do ar e sem edição. Reabra para voltar a trabalhar nele.
        </Aviso>
      ) : null}

      <div className="flex flex-wrap gap-2 mb-5" data-testid="acoes-do-artigo">
        {ACOES_DO_ESTADO[artigo.estado]
          .filter((item) => !item.publica || podePublicar)
          .map((item) => (
            <Botao
              key={item.acao}
              variante={item.acao === "publicar" ? "verde" : "fantasma"}
              disabled={agir.isPending}
              onClick={() =>
                agir.mutate(item.acao, {
                  onSuccess: () => setRecado(`Feito: ${ROTULO_DA_ACAO[item.acao].toLocaleLowerCase("pt-BR")}.`),
                })
              }
            >
              {ROTULO_DA_ACAO[item.acao]}
            </Botao>
          ))}
      </div>
      {agir.isError ? (
        <Aviso tom="erro" className="mb-4">
          {mensagemDoConsole(agir.error)}
        </Aviso>
      ) : null}
      {recado && !agir.isError ? (
        <Aviso tom="sucesso" className="mb-4" data-testid="recado-do-artigo">
          {recado}
        </Aviso>
      ) : null}

      {/* A chave recria o formulário quando o servidor devolve outra versão ou outro estado. */}
      <FormularioDoArtigo
        key={`${artigo.versao_atual}:${artigo.estado}:${artigo.atualizado_em}`}
        inicial={{
          titulo: artigo.titulo,
          resumo: artigo.resumo,
          corpo: artigo.corpo,
          categoria_id: artigo.categoria_id,
          modulo_chave: artigo.modulo_chave ?? null,
          visibilidade: artigo.visibilidade,
          organizacao_id: artigo.organizacao_id,
        }}
        desabilitado={desabilitado}
        rotuloDoBotao={noAr ? "Salvar e publicar a mudança" : "Salvar"}
        salvando={editar.isPending}
        erro={editar.error}
        aoSalvar={(corpo) =>
          editar.mutate(corpo, {
            onSuccess: (resultado) =>
              setRecado(
                resultado.nova_versao
                  ? `Versão ${resultado.artigo.versao_atual} criada e publicada.`
                  : "Alterações salvas.",
              ),
          })
        }
      />

      <Versoes artigo={artigo} />
      <Avaliacoes artigoId={artigo.id} />
    </div>
  );
}

export function EdicaoDeArtigo() {
  const { id = "" } = useParams();
  const artigo = useArtigoNoConsole(id);
  useTitulo(artigo.data?.titulo ?? "Artigo");

  if (artigo.isPending) return <p role="status">Carregando artigo…</p>;
  if (artigo.isError) {
    return (
      <div>
        <Aviso tom="erro">{mensagemDoConsole(artigo.error)}</Aviso>
        <p className="mt-4">
          <Link to="/admin/conhecimento">Voltar para a base de conhecimento</Link>
        </p>
      </div>
    );
  }
  return <CorpoDoEditor artigo={artigo.data} />;
}
